import { type EditorPlugin } from '../../types/aha-editor';
import { type Editor } from 'slate';
import AhaEditor from '../../AhaEditor';
import AudioComponentPlugin from '../MediaPlugin/components/AudioComponentPlugin';
import ImageComponentPlugin from '../MediaPlugin/components/ImageComponentPlugin';
import VideoComponentPlugin from '../MediaPlugin/components/VideoComponentPlugin';
import ToolInsertMedia from '../MediaPlugin/tools/ToolInsertMedia';

interface Attachment {
  url: string;
  name: string;
  fileExtension: string;
  mimeType: string;
  isImage: boolean;
}

export function findAttachmentsInHTML(html: string): Attachment[] {
  const ids = new Set<string>();

  // Label in list mode
  const namePattern = /attachment-name-(\d+)/g;
  const nameMatches = html.matchAll(namePattern);
  for (const match of nameMatches) {
    ids.add(match[1]);
  }

  // Grid mode or Icon in list mode
  const pathPattern = /\/attachments\/(\d+)/g;
  const pathMatches = html.matchAll(pathPattern);
  for (const match of pathMatches) {
    ids.add(match[1]);
  }

  return Array.from(ids)
    .map((id) => {
      // Make sure we can find an actual note attachment with this ID in
      // the document before dropping it into the editor.
      const img = document.querySelector(
        `#attachment-${id} img.attachment-thumbnail-image`
      ) as HTMLImageElement;

      if (img && img.style.display !== 'none') {
        return {
          url: img.getAttribute('data-original-src') ?? img.src,
          name: img.getAttribute('title') ?? 'Unnamed file',
          fileExtension: img.getAttribute('data-file-ext'),
          mimeType: img.getAttribute('data-content-type'),
          isImage: img.hasAttribute('data-is-image'),
        };
      }

      return null;
    })
    .filter((attachment) => Boolean(attachment?.url)) as Attachment[];
}

// Paste or drop for HTML.
const DropAttachmentsPlugin: EditorPlugin = {
  name: 'Drop attachments plugin',
  dependencies: [
    AudioComponentPlugin,
    ImageComponentPlugin,
    VideoComponentPlugin,
  ],
  insertData: (editor: Editor, data: DataTransfer, controller) => {
    const mediaProvider = controller?.ahaEditor.getProvider('media');

    if (!mediaProvider?.canAddAttachments) {
      return false;
    }

    if (data.types.includes(`application/${AhaEditor.clipboardFormatKey}`)) {
      // If copying from the editor, we don't want to handle the paste here.
      return false;
    }

    let html = data.getData('text/html');

    if (!html) {
      const url = data.getData('text/uri-list');
      if (url) {
        // COMPAT: Safari will sometimes only give us a URL. In that
        // case, we can search the document for the link it came from,
        // and use that link's HTML instead.
        const attachmentsElement = document.querySelector('.attachments');
        const element = attachmentsElement?.querySelector(
          `:scope a[href="${url}"]`
        );
        if (element) {
          html = element.outerHTML;
        }
      }
    }

    if (!html) return false;

    const attachments = findAttachmentsInHTML(html);
    if (attachments.length > 0) {
      attachments.forEach(({ url, name, fileExtension, mimeType, isImage }) => {
        void ToolInsertMedia.insertMediaFromUrl(editor, {
          url,
          fileExtension,
          name,
          mimeType,
        });
      });
      return true;
    }

    return false;
  },
};

export default DropAttachmentsPlugin;
